import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { cpfApplyMask } from "helpers/utils";
import {
  ROLES_PROTECTED,
  canAccessPanelFromPermissions,
  computeEffectivePermissions,
  ensureMandatoryRoleIds,
  isMandatoryRole,
  labelPermission,
  labelRole,
} from "helpers/accessControlLabels";
import { ROLES } from "helpers/permissions";
import {
  RoleWithPermissions,
  UserAccessDetail,
  syncUserRoles,
} from "services/rolesPermissions";
import {
  ACESSO_ACCENT,
  checkboxSx,
  infoBannerSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../controle-acesso.theme";

type Props = {
  open: boolean;
  user: {
    id: string;
    nome: string;
    email: string;
    cpf: string;
    avatar?: string | null;
  } | null;
  rolesCatalog: RoleWithPermissions[];
  initialAccess: UserAccessDetail | null;
  allowProtectedRoles: boolean;
  onClose: () => void;
  onSaved: () => void;
};

function userInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function UserInfoCard({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 1.5,
        bgcolor: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.2)",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: "rgba(249, 96, 48, 0.25)",
          color: "#fff",
        }}
      >
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography variant="caption" sx={{ opacity: 0.75, letterSpacing: 0.6 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} noWrap title={value}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

export default function UserAccessDialog({
  open,
  user,
  rolesCatalog,
  initialAccess,
  allowProtectedRoles,
  onClose,
  onSaved,
}: Props) {
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !initialAccess) return;
    setSelectedRoleIds(
      ensureMandatoryRoleIds(
        initialAccess.rolesDetail.map((r) => r.id),
        rolesCatalog,
      ),
    );
    setError(null);
  }, [open, initialAccess, rolesCatalog]);

  const assignableRoles = useMemo(
    () =>
      rolesCatalog.filter(
        (role) => allowProtectedRoles || !ROLES_PROTECTED.has(role.type),
      ),
    [rolesCatalog, allowProtectedRoles],
  );

  const effectivePermissions = useMemo(
    () =>
      computeEffectivePermissions(
        ensureMandatoryRoleIds(selectedRoleIds, rolesCatalog),
        rolesCatalog,
      ),
    [selectedRoleIds, rolesCatalog],
  );

  const willAccessPanel = canAccessPanelFromPermissions(effectivePermissions);

  const toggleRole = (roleId: string, roleType: string) => {
    if (isMandatoryRole(roleType)) return;
    setSelectedRoleIds((prev) =>
      ensureMandatoryRoleIds(
        prev.includes(roleId)
          ? prev.filter((id) => id !== roleId)
          : [...prev, roleId],
        rolesCatalog,
      ),
    );
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      await syncUserRoles(
        user.id,
        ensureMandatoryRoleIds(selectedRoleIds, rolesCatalog),
      );
      onSaved();
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e?.response?.data?.message || e?.message || "Erro ao salvar acesso do usuário.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const formattedCpf = user.cpf ? cpfApplyMask(user.cpf) : "—";
  const avatarSrc = user.avatar?.trim() || undefined;
  const initials = userInitials(user.nome) || "?";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0} flex={1}>
            <Avatar
              src={avatarSrc}
              alt={user.nome}
              imgProps={{ referrerPolicy: "no-referrer" }}
              sx={{
                width: 52,
                height: 52,
                bgcolor: "rgba(249, 96, 48, 0.3)",
                color: "#fff",
                fontWeight: 700,
                border: "2px solid rgba(255,255,255,0.3)",
              }}
            >
              {initials}
            </Avatar>
            <Box minWidth={0} flex={1}>
              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
                <ManageAccountsOutlinedIcon sx={{ fontSize: 18, opacity: 0.85 }} />
                <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                  Gerenciar acesso
                </Typography>
              </Stack>
              <Typography variant="h5" fontWeight="bold" noWrap title={user.nome}>
                {user.nome}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Grid container spacing={1.5} mt={1}>
          <Grid item xs={12} sm={7}>
            <UserInfoCard
              icon={<EmailOutlinedIcon fontSize="small" />}
              label="E-mail"
              value={user.email}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <UserInfoCard
              icon={<BadgeOutlinedIcon fontSize="small" />}
              label="CPF"
              value={formattedCpf}
            />
          </Grid>
        </Grid>
      </Box>

      <MDBox sx={{ p: 3, overflowY: "auto" }}>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <MDBox sx={infoBannerSx}>
            <MDTypography variant="caption" color="text.secondary">
              O perfil <strong>{labelRole(ROLES.ROLE_ATLETA)}</strong> é obrigatório para todos
              os usuários e não pode ser removido. Demais perfis são opcionais. Após salvar, o
              usuário deve fazer login novamente para aplicar as permissões.
            </MDTypography>
          </MDBox>

          <MDTypography variant="button" fontWeight="bold">
            Perfis de acesso
          </MDTypography>

          <FormGroup>
            {assignableRoles.map((role) => {
              const mandatory = isMandatoryRole(role.type);
              const checked = mandatory || selectedRoleIds.includes(role.id);
              return (
              <Box key={role.id} mb={1.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={mandatory}
                      onChange={() => toggleRole(role.id, role.type)}
                      sx={checkboxSx}
                    />
                  }
                  label={
                    <Box>
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2" fontWeight="medium">
                          {labelRole(role.type)}
                        </Typography>
                        {mandatory ? (
                          <Chip
                            size="small"
                            label="Obrigatório"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              bgcolor: "grey.200",
                              fontWeight: 600,
                            }}
                          />
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                  }
                />
                {role.permissions.length > 0 ? (
                  <Stack direction="row" flexWrap="wrap" gap={0.5} ml={4}>
                    {role.permissions.map((perm) => (
                      <Chip
                        key={perm.id}
                        label={labelPermission(perm.type)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Box>
            );
            })}
          </FormGroup>

          <Divider />

          <MDTypography variant="button" fontWeight="bold">
            Permissões efetivas (preview)
          </MDTypography>

          {effectivePermissions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma permissão — usuário não acessará o painel.
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {effectivePermissions.map((perm) => (
                <Chip
                  key={perm}
                  label={labelPermission(perm)}
                  size="small"
                  sx={{
                    bgcolor: "rgba(249, 96, 48, 0.1)",
                    color: ACESSO_ACCENT,
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>
          )}

          <Alert severity={willAccessPanel ? "success" : "warning"} variant="standard">
            {willAccessPanel
              ? "Este usuário poderá acessar o painel administrativo."
              : `Com apenas o perfil ${labelRole(ROLES.ROLE_ATLETA)}, o acesso fica restrito ao app/site de participante.`}
          </Alert>
        </Stack>
      </MDBox>

      <MDBox
        px={3}
        py={2}
        display="flex"
        justifyContent="flex-end"
        gap={1}
        borderTop="1px solid"
        borderColor="grey.200"
        bgcolor="grey.50"
      >
        <MDButton variant="outlined" color="dark" onClick={onClose}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={() => void handleSave()}
          disabled={saving}
          sx={saveButtonSx}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : "Salvar acesso"}
        </MDButton>
      </MDBox>
    </Dialog>
  );
}
